// referredByName.ts
//
// Reading a referral line into a person and a provider number.
//
// Lives apart from the dialog so it can be tested against the shapes production
// actually contains, and so the distribution list and the dialog cannot drift into
// splitting the same name two different ways.

// An AU Medicare provider number is exactly 8 characters: six digits, a location
// character and a check character (4063782H, 284145AX). An MO routing ID cannot match it
// — RI48600004V is 11. Mirrors AU_PROVIDER_NUMBER in Backend/v2/helper/addressedTo.js;
// the two must agree, or the addressee line and the HL7 id type disagree about the same
// number.
export const AU_PROVIDER_NUMBER = /^\d{6}[A-Za-z0-9][A-Za-z]$/;

/**
 * Titles and honorifics, dropped wherever they appear and whatever their case.
 *
 * A title left in front becomes part of the given name, and the receiving practice
 * matches on the name — so the report files as unallocated. Every one of these appears
 * on real referrals, in every case, with and without the dot.
 */
const NAME_TITLES = /\b(?:dr|doctor|prof|professor|mr|mrs|ms|miss|sr|sister|assoc|a\/prof)\.?\b/gi;

/**
 * The words of a person's name, with the noise a referral line collects stripped out.
 *
 * Handles what production actually contains: stray brackets from a half-typed number,
 * trailing separators, and "Surname, Given" where the comma states which half is which.
 */
export function nameWords(text: string): string[] {
  // Bracketed text is parenthetical — a preferred name, a practice, a second number —
  // and never part of the name itself. Dropped whole rather than flattened, or
  // "Dr Eun (Grace) Kang" reports a given name of "Eun Grace".
  let cleaned = text.replace(/\([^)]*\)/g, ' ').replace(NAME_TITLES, ' ');
  // A lone bracket left by a number the sender never finished typing ("Dr Arun Rao (").
  cleaned = cleaned.replace(/[()]/g, ' ');
  // "Surname, Given" — the comma says which half is the family name, so it wins over
  // position. Reversed here so the shared "last word is the surname" rule still applies.
  const comma = cleaned.indexOf(',');
  if (comma !== -1) {
    const before = cleaned.slice(0, comma).trim();
    const after = cleaned.slice(comma + 1).trim();
    if (before && after) cleaned = `${after} ${before}`;
    else cleaned = before || after;
  }
  return cleaned
    .replace(/[.,;:]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Pull "Dr Anup Kumar (4063782H)" apart into a name and a provider number.
 *
 * Used ONLY to supply a name for a number the sender typed themselves, and only when the
 * two numbers are identical — never to choose the recipient. Returns null unless both a
 * bracketed provider number and a name are present.
 */
export function parseReferredBy(
  referredBy?: string
): { id: string; firstName: string; lastName: string } | null {
  const raw = (referredBy || '').trim();
  if (!raw) return null;

  // The bracket that holds a PROVIDER NUMBER, not merely the first bracket. Referrals
  // carry other bracketed text — "Dr Eun (Grace) Kang (477948QW)" puts a preferred name
  // first — and cutting at the first bracket read the surname as "Eun".
  let id = '';
  let idAt = -1;
  for (const found of raw.matchAll(/\(([^)]*)\)/g)) {
    const candidate = found[1].trim();
    if (AU_PROVIDER_NUMBER.test(candidate)) {
      id = candidate;
      idAt = found.index ?? -1;
      break;
    }
  }
  if (!id || idAt === -1) return null;

  // Only the text BEFORE the number is the referrer. Anything after it is a copy-to
  // ("…(4592703J), CC: Dr K. Semple"), and reading on took the surname from the wrong
  // doctor, sending the report to the person copied in.
  const words = nameWords(raw.slice(0, idAt));
  if (words.length === 0) return null;

  // Surname last, everything before it the given name(s) — the order the demographic row
  // is written in.
  return {
    id,
    lastName: words[words.length - 1],
    firstName: words.slice(0, -1).join(' '),
  };
}
